import { useState, useCallback } from "react"
import { useKeycloak } from "@react-keycloak/web"
import axios from "axios"
import { useVistCaseStore } from "../stores/vistCaseStore"

export interface VistCaseMetadata {
    author: string
    version: string
    type: string
    module_id: string
    module_version: string
    case_id: string
    case_name: string
    creation_time: string
    platform_version: string
    discipline_level: string[]
    discipline_tags: string[]
    customer_exclusive: string
    history: any[]
    description: string
    language: string[]
    region: string[]
    guidance: string
    thumbnail: string
}

export interface VistCase {
    jobId: string
    filePath: string
    metadata: VistCaseMetadata
    fileSize: number
    lastModified: string
}

interface UseS3CasesReturn {
    cases: VistCase[]
    isLoading: boolean
    error: string | null
    fetchCases: () => Promise<void>
    refetch: () => Promise<void>
    clearError: () => void
    getDownloadUrl: (jobId: string) => Promise<string>
}

const API_BASE_URL = process.env.NODE_ENV === "development" ? "http://localhost:3001" : ""

export const useS3Cases = (): UseS3CasesReturn => {
    const { keycloak } = useKeycloak()
    const store = useVistCaseStore()
  
    const fetchCases = useCallback(async () => {
      const userId = keycloak.tokenParsed?.sub
      const token = keycloak.token
  
      if (!userId || !token || !keycloak.authenticated) {
        store.setServerError("User authentication required")
        return
      }
  
      store.setServerLoading(true)
      store.setServerError(null)
  
      try {
        // Fetch .vistcase files with just metadata extracted
        const response = await axios.get(`${API_BASE_URL}/api/s3/vistcases`, {
          headers: { authorization: `Bearer ${token}` }
        })
        
        store.setServerCases(response.data)
      } catch (err) {
        const errorMessage = axios.isAxiosError(err) 
          ? `Failed to load vistcases: ${err.response?.data?.message || err.message}`
          : "Failed to load your vistcases"
        store.setServerError(errorMessage)
        console.error("Failed to fetch S3 vistcases:", err)
      } finally {
        store.setServerLoading(false)
      }
    }, [keycloak.tokenParsed?.sub, keycloak.token, keycloak.authenticated])
  
    const refetch = useCallback(() => fetchCases(), [fetchCases])
  
    const clearError = useCallback(() => {
      store.setServerError(null)
    }, [])

    const getDownloadUrl = useCallback(async (jobId: string): Promise<string> => {
      if (!keycloak.authenticated) {
        throw new Error('Not authenticated')
      }

      const userPath = `case-it/userdata/${keycloak.tokenParsed?.sub}/cases/${jobId}.vistcase`
      
      // Use the existing GET endpoint instead of POST
      const response = await axios.get(
        `${API_BASE_URL}/api/s3/getPreSignedUrl/${encodeURIComponent(userPath)}`,
        {
          headers: {
            Authorization: `Bearer ${keycloak.token}`,
          },
        }
      )

      return response.data.url  // Note: returns 'url' not 'downloadUrl'
    }, [keycloak])
  
    return {
      cases: store.serverCases,
      isLoading: store.serverLoading,
      error: store.serverError,
      fetchCases,
      refetch,
      clearError,
      getDownloadUrl
    }
  }